import {useAtom, useSetAtom} from 'jotai';
import {useEffect, useRef, useState} from 'react';
import styled from 'styled-components';
import {
  mapCenterAtom,
  mapInstanceAtom,
  mapSideInfoAtom,
  zoomLevelAtom,
} from '@/map/atom';
import {useCreateMap, usePreventMapScrollEvent} from '@/map/hooks';
import {interceptProps} from '@/styled-components';
import {LAYOUT_SIZE} from '@/styles';
import {MAP_LAYER_Z_INDEX} from '../constants';
import {AiPrompt} from './AiPrompt';
import {ContextMenu} from './ContextMenu';
import {FloatingMenu} from './FloatingMenu';
import {LayerGroup} from './MapLayers';
import {QuickMenu} from './QuickMenu';
import {RegionController} from './RegionController';
import {Toolbox} from './Toolbox';

export const MAP_DOM_ID = 'dynamic-maps-container';

export const Map = () => {
  const {map, createMap} = useCreateMap();
  const [isInit, setInit] = useState<boolean>();
  const [zoom, setZoomLevel] = useAtom(zoomLevelAtom);
  const [center, setCenter] = useAtom(mapCenterAtom);
  const [sideInfo] = useAtom(mapSideInfoAtom);
  const setMapAtom = useSetAtom(mapInstanceAtom);

  const preventScrollEvent = usePreventMapScrollEvent();
  const idleEvent = useRef<naver.maps.MapEventListener>();

  useEffect(() => {
    const instance = createMap(MAP_DOM_ID);

    if (instance) {
      instance.addListenerOnce('init', () => {
        setInit(true);
      });

      return () => {
        instance.destroy();
      };
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!map) {
      return;
    }

    idleEvent.current = map.addListener('idle', () => {
      setZoomLevel(map.getZoom());
      const coords = map.getCenter();
      setCenter((prev) => (!prev || !coords.equals(prev) ? coords : prev));
    });

    return () => {
      if (idleEvent.current) {
        map.removeListener(idleEvent.current);
        idleEvent.current = undefined;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  useEffect(() => {
    if (map) {
      map.setOptions('scrollWheel', !preventScrollEvent);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, preventScrollEvent]);

  useEffect(() => {
    if (map && zoom && map.getZoom() !== zoom) {
      map.setZoom(zoom, false);
    }
  }, [map, zoom]);

  useEffect(() => {
    if (map && center && !map.getCenter().equals(center)) {
      map.setCenter(center);
    }
  }, [map, center]);

  useEffect(() => {
    setMapAtom(map);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  const handleSpace = !!sideInfo?.menu ? 24 : 0;
  const sidePenalSpace = sideInfo?.open ? LAYOUT_SIZE.SIDE_PANEL : 0;

  return (
    <Root>
      <MapContainer id={MAP_DOM_ID}>
        {isInit && !!map && <LayerGroup map={map} />}
      </MapContainer>
      <Toolbox />
      <AiPrompt map={map} />
      {!!map && <ContextMenu map={map} />}
      <MapOverlapLayer style={{left: sidePenalSpace + handleSpace + 8}}>
        <TopElementBox align="left">
          <FloatingMenu map={map} />
        </TopElementBox>
        <TopElementBox align="center">
          <RegionController map={map} />
        </TopElementBox>
        <TopElementBox align="right">
          <QuickMenu />
        </TopElementBox>
      </MapOverlapLayer>
    </Root>
  );
};

const Root = styled.div`
  position: relative;
  display: flex;
  flex-grow: 1;
  overflow: hidden;
`;

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  outline: 0;
`;

const TopElementBox = styled('div').withConfig({
  shouldForwardProp: interceptProps(['align']),
})<{
  align: 'left' | 'center' | 'right';
}>`
  position: absolute;
  top: 0px;
  pointer-events: auto;
  ${({align}) =>
    align === 'center'
      ? `left: 50%; transform: translateX(-50%);`
      : align === 'right'
      ? `right: 56px;`
      : `${align}: 0; bottom: 0; pointer-events: none;`}
  & > * {
    &:not(:first-of-type) {
      margin-top: 4px;
    }
  }
`;

const MapOverlapLayer = styled.div`
  position: absolute;
  z-index: ${MAP_LAYER_Z_INDEX.MAP_FLOATING};
  right: 16px;
  top: 16px;
  bottom: 16px;
  transition: left 100ms ease-in-out;
  pointer-events: none;
`;
